/**
 * Cambrian body parts — the physical evolutions picked at each growth step.
 *
 * Every part mounts at one fixed spot on the fish (so it always sits in the
 * right place anatomically) and levels 1 → MAX_PART_LEVEL. Each evolution
 * offers a mix of new parts and upgrades to ones already grown.
 */

export type PartId = 'stinger' | 'jaws' | 'fins' | 'tail' | 'quills' | 'thorns' | 'shield' | 'gills';

export const MAX_PART_LEVEL = 3;

/** Owned parts and their level (absent = not grown). */
export type PartLevels = Partial<Record<PartId, number>>;

export interface PartDef {
  id: PartId;
  name: string;
  /** Where it grows — shown on cards. */
  spot: string;
  color: number;
  /** One line per level (index 0 = level 1). */
  levels: string[];
  /** Short pitch shown on the card. */
  blurb: string;
}

export const PARTS: PartDef[] = [
  {
    id: 'stinger', name: 'Head Stinger', spot: 'Face', color: 0xff5a78,
    blurb: 'Lunge into anything bigger than you to sting it.',
    levels: ['Sting 8 + venom 2/s', 'Sting 16 + venom 4/s, barbed', 'Sting 30 + venom 8/s, venom sac'],
  },
  {
    id: 'jaws', name: 'Mandibles', spot: 'Mouth', color: 0xffc860,
    blurb: 'Hooked jaws at the mouth — harder, faster bites.',
    levels: ['Bite +40%', 'Bite +90%, lunge cooldown −10%', 'Bite +160%, lunge cooldown −20%'],
  },
  {
    id: 'fins', name: 'Pectoral Fins', spot: 'Sides', color: 0x60d8ff,
    blurb: 'Wide side fins — swim faster.',
    levels: ['Speed +12%', 'Speed +24%', 'Speed +38%, second fin pair'],
  },
  {
    id: 'tail', name: 'Power Tail', spot: 'Tail', color: 0x70f0c0,
    blurb: 'A stronger tail — longer lunges, faster ATP recharge for dodging.',
    levels: ['Lunge +12%, ATP recharge +25%', 'Lunge +24%, ATP recharge +50%', 'Lunge +40%, ATP recharge +80%, a 3rd dodge charge'],
  },
  {
    id: 'quills', name: 'Quill Launcher', spot: 'Back', color: 0xd0a0ff,
    blurb: 'Back quills that fire by themselves at nearby enemies.',
    levels: ['1 quill every 1.8s', '2 quills every 1.4s', '3 quills every 1.0s'],
  },
  {
    id: 'thorns', name: 'Thorn Spines', spot: 'Flanks', color: 0xff8a40,
    blurb: 'Spines along your sides — whatever hits you gets hurt.',
    levels: ['Attackers take 8', 'Attackers take 16 and get thrown back', 'Attackers take 30 and get thrown back'],
  },
  {
    id: 'shield', name: 'Chitin Shield', spot: 'Head plate', color: 0x80b0ff,
    blurb: 'A bubble shield that blocks a whole hit, then recharges.',
    levels: ['Blocks 1 hit, recharge 14s', 'Blocks 1 hit, recharge 10s', 'Blocks 2 hits, recharge 8s'],
  },
  {
    id: 'gills', name: 'Blood Gills', spot: 'Behind head', color: 0xff6060,
    blurb: 'Bigger gills — more health and faster healing.',
    levels: ['Max HP +20, regen +1.5/s', 'Max HP +40, regen +3/s', 'Max HP +70, regen +5/s'],
  },
];

export const PART_BY_ID = new Map(PARTS.map((p) => [p.id, p]));

/** Per-level numbers the stage reads (index = level; 0 = not grown). */
export const PART_STATS = {
  stingDamage: [0, 8, 16, 30],
  stingVenomDps: [0, 2, 4, 8],
  jawsBiteMult: [1, 1.4, 1.9, 2.6],
  jawsCooldownMult: [1, 1, 0.9, 0.8],
  finsSpeed: [0, 0.12, 0.24, 0.38],
  tailLungeMult: [1, 1.12, 1.24, 1.4],
  tailAtpMaxMult: [1, 1, 1, 1.5],
  tailAtpRegenMult: [1, 1.25, 1.5, 1.8],
  quillCount: [0, 1, 2, 3],
  quillInterval: [0, 1.8, 1.4, 1.0],
  quillDamage: [0, 5, 7, 10],
  thornDamage: [0, 8, 16, 30],
  thornKnockback: [0, 0, 260, 380],
  shieldCharges: [0, 1, 1, 2],
  shieldRecharge: [0, 14, 10, 8],
  gillsHp: [0, 20, 40, 70],
  gillsRegen: [0, 1.5, 3, 5],
} as const;

export function partLevel(parts: PartLevels, id: PartId): number {
  return parts[id] ?? 0;
}

/** Up to `n` distinct evolution offers: new parts and upgrades, shuffled. */
export function rollOffers(parts: PartLevels, n = 3): PartId[] {
  const pool = PARTS.map((p) => p.id).filter((id) => partLevel(parts, id) < MAX_PART_LEVEL);
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, n);
}

/** Small inline SVG glyph per part for cards and the body-plan screen. */
export function partIconSvg(id: PartId, color: number): string {
  const c = `#${color.toString(16).padStart(6, '0')}`;
  const body = '<ellipse cx="20" cy="24" rx="13" ry="8" fill="#f9c876" opacity="0.35"/>';
  const glyph: Record<PartId, string> = {
    stinger: `<path d="M20 26 L20 4" stroke="${c}" stroke-width="3" stroke-linecap="round"/><path d="M16 12 L20 3 L24 12 Z" fill="${c}"/><circle cx="20" cy="27" r="4" fill="${c}" opacity="0.7"/>`,
    jaws: `<path d="M8 10 Q20 22 18 32" stroke="${c}" stroke-width="3.5" fill="none" stroke-linecap="round"/><path d="M32 10 Q20 22 22 32" stroke="${c}" stroke-width="3.5" fill="none" stroke-linecap="round"/>`,
    fins: `<path d="M20 22 L4 8 L10 26 Z" fill="${c}" opacity="0.85"/><path d="M20 22 L36 8 L30 26 Z" fill="${c}" opacity="0.85"/>`,
    tail: `<path d="M20 22 L8 38 L20 32 L32 38 Z" fill="${c}"/><path d="M20 10 L20 24" stroke="${c}" stroke-width="3"/>`,
    quills: `<path d="M12 30 L8 8 M20 30 L20 4 M28 30 L32 8" stroke="${c}" stroke-width="3" stroke-linecap="round"/><circle cx="8" cy="8" r="2" fill="#fff"/><circle cx="20" cy="4" r="2" fill="#fff"/><circle cx="32" cy="8" r="2" fill="#fff"/>`,
    thorns: `<path d="M6 24 L2 14 L10 22 M14 18 L12 6 L18 17 M26 17 L28 6 L22 18 M34 24 L38 14 L30 22" stroke="${c}" stroke-width="3" fill="none" stroke-linejoin="round"/>`,
    shield: `<circle cx="20" cy="22" r="16" fill="none" stroke="${c}" stroke-width="2.5"/><path d="M20 8 L31 14 L31 26 L20 34 L9 26 L9 14 Z" fill="${c}" opacity="0.35"/>`,
    gills: `<path d="M12 12 Q8 22 12 32 M18 12 Q14 22 18 32 M24 12 Q20 22 24 32" stroke="${c}" stroke-width="3" fill="none" stroke-linecap="round"/><circle cx="30" cy="22" r="4" fill="${c}"/>`,
  };
  return `<svg viewBox="0 0 40 40" width="100%" height="100%">${body}${glyph[id]}</svg>`;
}
